import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Task } from '../types';
import Header from '../components/Header';
import '../styles/Calendar.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

interface WeekView {
  startDate: Date;
  endDate: Date;
  days: Date[];
}

const Calendar: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState<WeekView>(getCurrentWeek());
  const [engineerColors, setEngineerColors] = useState<Map<number, string>>(new Map());

  // Get current week (Monday to Friday)
  function getCurrentWeek(date: Date = new Date()): WeekView {
    const current = new Date(date);
    const day = current.getDay();
    const diff = current.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
    const monday = new Date(current.setDate(diff));
    monday.setHours(0, 0, 0, 0);

    const days: Date[] = [];
    for (let i = 0; i < 5; i++) { // Only Monday to Friday
      const day = new Date(monday);
      day.setDate(monday.getDate() + i);
      days.push(day);
    }

    const friday = new Date(days[4]);
    friday.setHours(23, 59, 59, 999);

    return {
      startDate: monday,
      endDate: friday,
      days
    };
  }

  // Generate consistent colors for engineers
  const generateEngineerColors = (tasks: Task[]) => {
    // Specific colors for each engineer by name
    const engineerColorsByName: { [key: string]: string } = {
      'James Keay': '#1e5631', // Dark green
      'Luke Mann': '#87CEEB', // Light blue
      'Sam Fuller': '#dc2626', // Red
      'Tom Wilks': '#ca8a04', // Dark yellow
    };

    const defaultColors = [
      '#3b82f6', // blue
      '#10b981', // green
      '#f59e0b', // amber
      '#8b5cf6', // violet
      '#ec4899', // pink
      '#06b6d4', // cyan
    ];

    const colorMap = new Map<number, string>();
    const uniqueEngineers = new Map<number, string>(); // engineerId -> name

    tasks.forEach(task => {
      if (task.assigned_user?.id) {
        const fullName = `${task.assigned_user.first_name} ${task.assigned_user.last_name}`;
        uniqueEngineers.set(task.assigned_user.id, fullName);
      }
      task.assigned_engineers?.forEach(eng => {
        const fullName = `${eng.first_name} ${eng.last_name}`;
        uniqueEngineers.set(eng.id, fullName);
      });
    });

    let defaultColorIndex = 0;
    Array.from(uniqueEngineers.entries()).forEach(([engineerId, engineerName]) => {
      // Check if engineer has a specific color assigned
      if (engineerColorsByName[engineerName]) {
        colorMap.set(engineerId, engineerColorsByName[engineerName]);
      } else {
        // Use default color for engineers not in the list
        colorMap.set(engineerId, defaultColors[defaultColorIndex % defaultColors.length]);
        defaultColorIndex++;
      }
    });

    setEngineerColors(colorMap);
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data || []);
      generateEngineerColors(response.data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTasksForDate = (date: Date): Task[] => {
    const dateStr = formatDateForComparison(date);
    return tasks.filter(task => {
      if (!task.scheduled_date) return false;
      const taskDateStr = task.scheduled_date.split('T')[0];
      return taskDateStr === dateStr;
    });
  };

  const formatDateForComparison = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const groupTasksByEngineer = (tasks: Task[]): Map<string, Task[]> => {
    const grouped = new Map<string, Task[]>();

    tasks.forEach(task => {
      // Handle assigned_engineers (multiple engineers)
      if (task.assigned_engineers && task.assigned_engineers.length > 0) {
        task.assigned_engineers.forEach(engineer => {
          const key = `${engineer.id}-${engineer.first_name} ${engineer.last_name}`;
          if (!grouped.has(key)) {
            grouped.set(key, []);
          }
          grouped.get(key)!.push(task);
        });
      }
      // Handle assigned_user (single engineer)
      else if (task.assigned_user) {
        const key = `${task.assigned_user.id}-${task.assigned_user.first_name} ${task.assigned_user.last_name}`;
        if (!grouped.has(key)) {
          grouped.set(key, []);
        }
        grouped.get(key)!.push(task);
      }
      // Unassigned tasks
      else {
        if (!grouped.has('unassigned-Unassigned')) {
          grouped.set('unassigned-Unassigned', []);
        }
        grouped.get('unassigned-Unassigned')!.push(task);
      }
    });

    return grouped;
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentWeek.startDate);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeek(getCurrentWeek(newDate));
  };

  const goToToday = () => {
    setCurrentWeek(getCurrentWeek());
  };

  const formatDateHeader = (date: Date): string => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
  };

  const getEngineerColor = (engineerId: number | string): string => {
    if (engineerId === 'unassigned') return '#6b7280'; // gray
    return engineerColors.get(Number(engineerId)) || '#6b7280';
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const formatTime = (timeString: string | undefined): string => {
    if (!timeString) return '';

    // Handle full datetime strings (e.g., "0000-01-01T05:00:00Z")
    if (timeString.includes('T')) {
      const timePart = timeString.split('T')[1];
      if (timePart.includes(':')) {
        const parts = timePart.split(':');
        return `${parts[0]}:${parts[1]}`;
      }
      return timePart;
    }

    // Handle simple time strings (e.g., "05:00:00")
    if (timeString.includes(':')) {
      const parts = timeString.split(':');
      return `${parts[0]}:${parts[1]}`;
    }

    return timeString;
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="calendar-container">
          <div className="loading">Loading calendar...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="calendar-container">
      <div className="calendar-header">
        <h1>Job Calendar</h1>
        <div className="calendar-navigation">
          <button onClick={() => navigateWeek('prev')}>Previous Week</button>
          <button onClick={goToToday}>Today</button>
          <button onClick={() => navigateWeek('next')}>Next Week</button>
        </div>
        <div className="week-range">
          {currentWeek.startDate.toLocaleDateString()} - {currentWeek.endDate.toLocaleDateString()}
        </div>
      </div>

      <div className="calendar-grid">
        {currentWeek.days.map((date, index) => {
          const dayTasks = getTasksForDate(date);
          const groupedTasks = groupTasksByEngineer(dayTasks);
          const today = isToday(date);

          return (
            <div key={index} className={`calendar-day ${today ? 'today' : ''}`}>
              <div className="day-header">
                <div className="day-name">{formatDateHeader(date)}</div>
                <div className="task-count">{dayTasks.length} job{dayTasks.length !== 1 ? 's' : ''}</div>
              </div>

              <div className="day-tasks">
                {groupedTasks.size === 0 ? (
                  <div className="no-tasks">No jobs scheduled</div>
                ) : (
                  Array.from(groupedTasks.entries()).map(([engineerKey, engineerTasks]) => {
                    const [engineerId, engineerName] = engineerKey.split('-');
                    const color = getEngineerColor(engineerId);

                    return (
                      <div key={engineerKey} className="engineer-group">
                        <div className="engineer-header" style={{ backgroundColor: color }}>
                          {engineerName} ({engineerTasks.length})
                        </div>
                        <div className="engineer-tasks">
                          {engineerTasks.map(task => (
                            <div
                              key={task.id}
                              className="calendar-task"
                              style={{ borderLeftColor: color }}
                            >
                              <div className="task-time">
                                {task.start_time || task.end_time ? (
                                  <>
                                    {formatTime(task.start_time) || 'TBC'}
                                    {task.end_time && ` - ${formatTime(task.end_time)}`}
                                  </>
                                ) : 'No time'}
                              </div>
                              <div className="task-title">{task.title}</div>
                              <div className="task-client">{task.client}</div>
                              <div className="task-address">{task.address_line1}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
    </div>
  );
};

export default Calendar;
